import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors } from '../../shared/theme/ticketColors';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { TicketSheet } from '../../shared/ticket/TicketSheet';
import { TicketButton } from '../../shared/ticket/TicketButton';
import { ticketControls } from '../../shared/ticket/ticketControls.styles';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import {
  listCustomerPackageEntitlements,
  voidPackageEntitlement,
} from '../../appointments/infra/servicePackagesRepo';
import type { CustomerPackageEntitlement } from '../../appointments/domain/servicePackageTypes';

type Props = {
  customerId: string;
  mobile?: boolean;
};

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-AR');
}

function statusLabel(status: string): { text: string; tone: 'ok' | 'warn' | 'off' } {
  switch (status) {
    case 'active':
      return { text: 'Activo', tone: 'ok' };
    case 'exhausted':
      return { text: 'Agotado', tone: 'off' };
    case 'expired':
      return { text: 'Vencido', tone: 'warn' };
    case 'void':
      return { text: 'Anulado', tone: 'off' };
    case 'pending_payment':
      return { text: 'Pago pendiente', tone: 'warn' };
    default:
      return { text: status, tone: 'off' };
  }
}

/**
 * Paquetes del cliente (capability `service_packages`): sesiones restantes,
 * vencimiento y anulación con motivo. Solo visible con la capability ON.
 */
export function CustomerPackagesPanel({ customerId, mobile = false }: Props) {
  const hasCap = useHasBusinessCapability('service_packages');
  const [items, setItems] = useState<CustomerPackageEntitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [voidTarget, setVoidTarget] = useState<CustomerPackageEntitlement | null>(null);
  const [voidBusy, setVoidBusy] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const load = useCallback(async () => {
    if (!hasCap || !customerId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await listCustomerPackageEntitlements({
        customerId,
        includeInactive: true,
      });
      setItems(rows);
    } catch (e) {
      presentUnexpectedFailure({
        module: 'customers',
        op: 'packages.list',
        error: e,
        title: 'Paquetes',
        userMessage: 'No se pudieron cargar los paquetes del cliente.',
      });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [customerId, hasCap]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!hasCap) return null;

  const canVoid = (e: CustomerPackageEntitlement) =>
    e.status === 'active' || e.status === 'exhausted' || e.status === 'pending_payment';

  const confirmVoid = async () => {
    if (!voidTarget) return;
    const reason = voidReason.trim();
    if (reason.length < 4) {
      showError('Anular paquete', 'Poné un motivo (mínimo 4 caracteres).');
      return;
    }
    setVoidBusy(true);
    try {
      await voidPackageEntitlement({
        entitlementId: voidTarget.id,
        reason,
        voidSale: true,
      });
      showSuccess('Paquete anulado');
      setVoidTarget(null);
      setVoidReason('');
      await load();
    } catch (e) {
      presentUnexpectedFailure({
        module: 'customers',
        op: 'packages.void',
        error: e,
        title: 'Anular paquete',
        userMessage:
          e instanceof Error && e.message ? e.message : 'No se pudo anular el paquete.',
      });
    } finally {
      setVoidBusy(false);
    }
  };

  const panelStyle = mobile ? styles.panelMobile : modalExperience.detailFlatSection;

  return (
    <View style={panelStyle} testID="customers.detail.packages">
      <Text style={mobile ? styles.titleMobile : modalExperience.detailBlockTitle}>
        Paquetes
      </Text>
      {loading ? <Text style={styles.meta}>Consultando paquetes…</Text> : null}
      {!loading && items.length === 0 ? (
        <Text style={styles.meta}>No tiene paquetes comprados.</Text>
      ) : null}
      {items.map((e) => {
        const st = statusLabel(e.status);
        return (
          <View key={e.id} style={styles.row} testID={`customers.detail.packages.${e.id}`}>
            <View style={{ flex: 1, gap: 2 }}>
              <View style={styles.rowTop}>
                <Text style={styles.rowTitle} numberOfLines={1}>
                  {e.packageName || 'Paquete'}
                </Text>
                <Text style={[styles.badge, st.tone === 'ok' && styles.badgeOk, st.tone === 'warn' && styles.badgeWarn]}>
                  {st.text}
                </Text>
              </View>
              <Text style={styles.meta}>
                {e.sessionsRemaining}/{e.sessionsTotal} sesiones
                {e.expiresAt ? ` · vence ${formatExpiry(e.expiresAt)}` : ''}
              </Text>
            </View>
            {canVoid(e) ? (
              <TouchableOpacity
                onPress={() => {
                  setVoidTarget(e);
                  setVoidReason('');
                }}
                hitSlop={16}
                testID={`customers.detail.packages.${e.id}.void`}
                accessibilityLabel={`Anular ${e.packageName || 'paquete'}`}
              >
                <Text style={styles.voidLink}>Anular</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        );
      })}

      <TicketSheet
        visible={voidTarget !== null}
        title="Anular paquete"
        subtitle={
          voidTarget
            ? voidTarget.status === 'pending_payment'
              ? `${voidTarget.packageName || 'Paquete'} · pago todavía no acreditado`
              : `${voidTarget.packageName || 'Paquete'} · se anula también la venta registrada`
            : undefined
        }
        onClose={() => {
          if (voidBusy) return;
          setVoidTarget(null);
        }}
        fitContent
        footer={
          <View style={styles.sheetActions}>
            <TicketButton
              label="Cancelar"
              variant="ghost"
              onPress={() => setVoidTarget(null)}
              style={{ flex: 1 }}
              disabled={voidBusy}
            />
            <TicketButton
              label={voidBusy ? 'Anulando…' : 'Anular paquete'}
              variant="danger"
              testID="customers.detail.packages.void.confirm"
              onPress={() => void confirmVoid()}
              disabled={voidBusy || voidReason.trim().length < 4}
              style={{ flex: 1 }}
            />
          </View>
        }
      >
        <View style={{ gap: spacing.sm, paddingBottom: spacing.sm }}>
          <Text style={styles.meta}>Motivo (mínimo 4 caracteres)</Text>
          <TextInput
            style={[ticketControls.input, styles.reasonInput]}
            value={voidReason}
            onChangeText={setVoidReason}
            placeholder="Ej. cobro duplicado"
            placeholderTextColor={ticketColors.ink3}
            editable={!voidBusy}
            testID="customers.detail.packages.void.reason"
            multiline
          />
        </View>
      </TicketSheet>
    </View>
  );
}

const styles = StyleSheet.create({
  panelMobile: {
    padding: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.border,
    gap: 6,
  },
  titleMobile: {
    fontSize: 12,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  meta: { color: ticketColors.ink3, fontSize: 13 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: 4,
  },
  rowTop: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  rowTitle: { flex: 1, fontSize: 14, fontWeight: '600', color: ticketColors.ink },
  badge: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    overflow: 'hidden',
  },
  badgeOk: { color: ticketColors.ok },
  badgeWarn: { color: ticketColors.warn },
  voidLink: { color: ticketColors.bad, fontSize: 13, fontWeight: '600' },
  sheetActions: { flexDirection: 'row', gap: spacing.sm },
  reasonInput: { minHeight: 72, textAlignVertical: 'top' },
});
